<template>
    <div>
        <v-alert color="info" text>
            {{ $t('Avainsanoilla voidaan luokitella ja suodattaa uutisia.') }}
        </v-alert>

        <!-- Tags Enabled Switch -->
        <v-col cols="auto">
            <v-switch :value="tagsEnabled" :label="$t('Avainsanat käytössä')" color="primary" :true-value="true"
                :false-value="false" :loading="updatingTagsStatus" @change="onTagsEnabledChange"
                :disabled="updatingTagsStatus"></v-switch>
        </v-col>

        <template v-if="tagsEnabled">
            <v-btn color="primary" class="mb-4" @click="newTag">
                {{ $t("Luo uusi avainsana") }}
            </v-btn>

            <v-data-table :headers="headers" :items="tags" :loading="loading" :no-data-text="$t('Ei tageja')">
                <template v-slot:item.name="{ item }">
                    <a @click="editTag(item.id)">
                        {{ getTagTitle(item) }}
                    </a>
                </template>


            </v-data-table>

            <!-- Tag Form Dialog -->
            <TagFormDialog :show="formDialogVisible" :tag="selectedTag" :key="tagFormKey" :mode="dialogMode"
                @save="onTagSaved" @cancel="onTagDialogCancel" @close="onTagDialogClose" @delete="onTagDeleted" />

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="showDeleteDialog" max-width="400px">
                <v-card>
                    <v-card-title>
                        <span class="text-h5">{{ $t('Poista avainsana') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <p>{{ $t('Haluatko varmasti poistaa avainsanan') }} "{{ getTagTitle(tagToDelete) }}"?</p>
                        <p class="text-caption text--secondary">{{ $t('Tätä toimintoa ei voi peruuttaa.') }}</p>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey darken-1" text @click="showDeleteDialog = false">
                            {{ $t('Peruuta') }}
                        </v-btn>
                        <v-btn color="error" @click="deleteTag" :loading="isDeleting">
                            {{ $t('Poista') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useTranslations } from "@/@core/libs/i18n";
import useAuth from "@/useAuth";
import TagFormDialog from "@/components/TagFormDialog.vue";
import { mdiPlus, mdiDelete, mdiCloseCircle } from "@mdi/js";

export default {
    name: "TagManagement",
    components: {
        TagFormDialog
    },
    setup() {
        const { t } = useTranslations();
        const { tenant } = useAuth();

        // State
        const loading = ref(false);
        const tags = ref([]);
        const editTagId = ref(null);
        const selectedTag = ref(null);
        const formDialogVisible = ref(false);
        const dialogMode = ref('create');
        const showDeleteDialog = ref(false);
        const tagToDelete = ref(null);
        const isDeleting = ref(false);
        const tagsEnabled = ref(false);
        const updatingTagsStatus = ref(false);
        const tagFormKey = ref("");

        // Get available languages from tenant
        const availableLanguages = computed(() => {
            return tenant.value?.languages || [];
        });

        // Determine primary language (first language or fallback)
        const primaryLanguage = computed(() => {
            return availableLanguages.value.length > 0 ? availableLanguages.value[0].code : 'fi';
        });

        // Table headers
        const headers = ref([
            {
                text: t('Nimi'),
                value: 'name',
            },
        ]);

        // Methods
        const fetchTags = async () => {
            loading.value = true;
            try {
                const response = await adminApi.getTags();
                if (response.ok) {
                    tags.value = response.data.results || response.data;
                } else {
                    alert(response.error);
                }
            } catch (error) {
                console.error("Error fetching tags:", error);
            } finally {
                loading.value = false;
            }
        };

        const getTagTranslation = (tag, languageCode) => {
            return tag.translations?.[languageCode]?.title || '';
        };

        const getTagTitle = (tag) => {
            if (!tag) return '';

            // Try to get title in primary language first
            const primaryTitle = getTagTranslation(tag, primaryLanguage.value);
            if (primaryTitle) return primaryTitle;

            // Fallback to first available translation
            for (const lang of availableLanguages.value) {
                const title = getTagTranslation(tag, lang.code);
                if (title) return title;
            }

            return tag.title || '';
        };

        const editTag = async (id) => {
            tagFormKey.value = `tag-${id}`;
            const res = await adminApi.getTag(id)
            selectedTag.value = res.data;
            editTagId.value = id;
            dialogMode.value = 'edit';
            formDialogVisible.value = true;
        };

        const newTag = () => {
            tagFormKey.value = "new-tag";
            selectedTag.value = null;
            editTagId.value = null;
            dialogMode.value = 'create';
            formDialogVisible.value = true;
        };

        const onTagDeleted = () => {
            formDialogVisible.value = false;
            selectedTag.value = null;
            editTagId.value = null;
            fetchTags();
        }

        const onTagSaved = (savedTag) => {
            formDialogVisible.value = false;

            if (dialogMode.value === 'create') {
                // Add new tag to list
                tags.value.push(savedTag);
            } else {
                // Update existing tag in list
                const index = tags.value.findIndex(tag => tag.id === savedTag.id);
                if (index > -1) {
                    tags.value.splice(index, 1, savedTag);
                }
            }

            selectedTag.value = null;
            editTagId.value = null;
        };

        const onTagDialogCancel = () => {
            formDialogVisible.value = false;
            selectedTag.value = null;
            editTagId.value = null;
        };

        const onTagDialogClose = () => {
            formDialogVisible.value = false;
            selectedTag.value = null;
            editTagId.value = null;
        };

        const confirmDelete = (tag) => {
            tagToDelete.value = tag;
            showDeleteDialog.value = true;
        };

        const deleteTag = async () => {
            if (!tagToDelete.value) return;

            isDeleting.value = true;
            try {
                const response = await adminApi.deleteTag(tagToDelete.value.id);
                if (response.ok) {
                    // Remove tag from list
                    const index = tags.value.findIndex(tag => tag.id === tagToDelete.value.id);
                    if (index > -1) {
                        tags.value.splice(index, 1);
                    }
                    showDeleteDialog.value = false;
                    tagToDelete.value = null;
                } else {
                    console.error("Error deleting tag:", response.error);
                    alert(response.error || t('Tagin poistaminen epäonnistui'));
                }
            } catch (error) {
                console.error("Error deleting tag:", error);
                alert(t('Tagin poistaminen epäonnistui'));
            } finally {
                isDeleting.value = false;
            }
        };

        const onTagsEnabledChange = async (value) => {
            updatingTagsStatus.value = true;
            const previousValue = tagsEnabled.value;

            const response = await adminApi.updateTagsSettings(value);
            if (response.ok) {
                tagsEnabled.value = value;
                tenant.value.settings.tags_enabled = value;
            } else {
                tagsEnabled.value = previousValue;
                console.error("Error updating tags settings:", response.error);
                alert(response.error || t('Avainsana-asetusten päivittäminen epäonnistui'));
            }
            updatingTagsStatus.value = false;
        };

        watch(tagsEnabled, () => {
            if (tagsEnabled.value)
                fetchTags();
        });

        // Lifecycle
        onMounted(() => {
            tagsEnabled.value = tenant.value.settings.tags_enabled;
        });

        return {
            loading,
            tags,
            editTagId,
            tenant,
            tagFormKey,
            onTagDeleted,
            selectedTag,
            formDialogVisible,
            dialogMode,
            showDeleteDialog,
            tagToDelete,
            isDeleting,
            tagsEnabled,
            availableLanguages,
            primaryLanguage,
            headers,
            updatingTagsStatus,
            fetchTags,
            getTagTranslation,
            getTagTitle,
            editTag,
            newTag,
            onTagSaved,
            onTagDialogCancel,
            onTagDialogClose,
            confirmDelete,
            deleteTag,
            onTagsEnabledChange,
            icons: {
                mdiPlus,
                mdiDelete,
                mdiCloseCircle
            }
        };
    }
};
</script>

<style scoped>
.v-data-table {
    width: 100%;
}
</style>
